import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import useStyles from "./styles";
import { CustomTypography } from "../../../../components/CustomTypography/CustomTypography";
import { colors } from "../../../../styles/colors";
import { getCurrentFleetId } from "../../../../utils/getCurrentFleet";
import {
  useGetAssignmentHistory,
  type TAssignmentHistoryEntry,
} from "../../../../api/employee";

/**
 * Historique d'affectation d'un collaborateur.
 *
 * CE PANNEAU N'A JAMAIS RIEN AFFICHE
 * ----------------------------------
 * Il existait depuis le depart, mais sa boucle de rendu etait commentee et
 * aucun appel ne l'alimentait : il etait vide pour TOUS les collaborateurs,
 * sans erreur ni trace, parce qu'il n'y avait rien derriere.
 *
 * La donnee ne pouvait pas venir de la liste des vehicules du collaborateur :
 * celle-ci ne renvoie que les affectations VIVANTES -- volontairement, sinon la
 * fiche affiche un vehicule rendu depuis des mois. Un historique demande
 * l'inverse. D'ou une route dediee, /fleets/{id}/employees/{id}/vehicles/history.
 */
type Props = {};

/** `2026-07-08 00:00:00` ou `2026-07-08` -> `08/07/2026`. */
const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const isActive = (entry: TAssignmentHistoryEntry): boolean =>
  entry.is_active === true || Number(entry.is_active) === 1;

export const HistoriqueContent: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { id = "" } = useParams();
  const fleetId = getCurrentFleetId();
  const { history, isPending } = useGetAssignmentHistory(fleetId, Number(id));

  return (
    <div className={classes.containerHistorique}>
      <div>
        <CustomTypography
          text={t("collaborator.assignmentHistory", "Assignment history")}
          variant="h5"
          color={colors.primary}
          isUppercase
        />
      </div>

      <div className={classes.historiqueContent}>
        {isPending && (
          <CustomTypography
            text={t("common.loading", "Loading…")}
            variant="link"
          />
        )}

        {/*
          Vide et charge : on le dit. Un panneau blanc laisse croire a une
          panne, alors qu'un collaborateur qui n'a jamais eu de vehicule est
          un cas parfaitement normal -- et c'est ce doute qui a fait remonter
          le probleme.
        */}
        {!isPending && history.length === 0 && (
          <CustomTypography
            text={t("collaborator.noAssignmentHistory", "No assignment yet")}
            variant="link"
            color={colors.textSecondary}
          />
        )}

        {history.map((entry) => (
          <div key={entry.assignmentId} className={classes.entry}>
            <div className={classes.entryHeader}>
              <CustomTypography
                text={
                  [entry.brand, entry.brandModel].filter(Boolean).join(" ") ||
                  entry.immatriculation ||
                  t("vehicule.vehicle", "Vehicle")
                }
                variant="default"
                fontWeight="bold"
              />
              {/*
                Une affectation en cours est signalee ici plutot que deduite de
                l'absence de date de fin : une affectation a duree determinee a
                une date de fin ET reste active jusqu'a ce qu'elle passe. Lire
                "termine" sur une affectation qui court encore est precisement
                le genre d'ecart qui a coute une demi-journee sur la fiche
                vehicule.
              */}
              {isActive(entry) && (
                <span className={classes.badge}>
                  {t("collaborator.assignmentOngoing", "Ongoing")}
                </span>
              )}
            </div>

            {entry.immatriculation && (
              <CustomTypography
                text={entry.immatriculation}
                variant="link"
                color={colors.textSecondary}
              />
            )}

            <CustomTypography
              text={`${formatDate(entry.start_date)} → ${formatDate(entry.end_date)}`}
              variant="link"
              color={colors.textSecondary}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
